// Pure helpers of the identity faces card (PRD-0031 slice 01).
import type {
  FaceMatchingCandidate,
  FaceMatchingIdentityDetail,
  FaceMatchingReference,
} from '@/services/api/face-matching'

/** What the card's header states: the counts and the reference set size. */
export interface FacesSummary {
  confirmed: number
  /** Every pending suggestion of the identity, not only the ones the card lists. */
  suggested: number
  references: number
  /** How many of the pending suggestions earn the *suggested match* label (ADR-0040). */
  suggestedMatches: number
}

export function facesSummary(detail: FaceMatchingIdentityDetail | undefined): FacesSummary {
  const references = detail?.references ?? []
  return {
    confirmed: references.length,
    suggested: detail?.pending_total ?? 0,
    references: references.filter(reference => reference.reference).length,
    suggestedMatches: detail?.suggested_matches ?? 0,
  }
}

/**
 * Only a confirmed face may become the main photo — the API answers 409 for the
 * rest, so the card never offers the action on a suggestion.
 */
export function canBeMainPhoto(
  face: FaceMatchingReference | FaceMatchingCandidate,
  detail: FaceMatchingIdentityDetail | undefined
): boolean {
  return (detail?.references ?? []).some(reference => reference.face_id === face.face_id)
}

/**
 * The face the avatar shows: this session's pick, else the stored main photo,
 * else the person's first confirmed reference.
 */
export function mainFaceId(
  detail: FaceMatchingIdentityDetail | undefined,
  chosen: string | null | undefined
): string | null {
  const references = detail?.references ?? []
  const isReference = (face_id: string | null | undefined) =>
    Boolean(face_id) && references.some(reference => reference.face_id === face_id)
  // The pick of this session wins over the stored one: the card badges the new
  // main photo the moment the call succeeds, before the detail query refetches.
  if (isReference(chosen)) return chosen ?? null
  if (isReference(detail?.main_face_id)) return detail?.main_face_id ?? null
  return references[0]?.face_id ?? null
}
